/**
 * GenerateProformaModal — sub-modal for setting the payment due date when
 * generating a proforma for an order.
 *
 * @see AC-4 in docs/specifications/2026-09-07-proforma-reczny-termin-platnosci/spec.md
 */
import React, { useEffect, useRef, useState, useId } from "react";
import { useGenerateBillingProformaMutation } from "../../../../hooks/useAdminProductCatalog";
import type { BillingOrderDetail } from "../../../../types/adminBilling";
import { todayInWarsaw } from "../utils/orderFormatters";
import {
  getDefaultProformaDueDate,
  getProformaDueDateRange,
} from "../utils/orderActions";

// Props

export interface GenerateProformaModalProps {
  orderId: string;
  brandId: string;
  order: BillingOrderDetail;
  onClose: () => void;
  /** Ref to the trigger button — focus is returned here on close */
  triggerRef?: React.RefObject<HTMLButtonElement | null>;
}

// Component

export const GenerateProformaModal: React.FC<GenerateProformaModalProps> = ({
  orderId,
  brandId,
  order,
  onClose,
  triggerRef,
}) => {
  const titleId = useId();
  const today = todayInWarsaw();
  const { min, max } = getProformaDueDateRange(today);
  const [dueDate, setDueDate] = useState(() =>
    getDefaultProformaDueDate(order, today)
  );

  const dateInputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  const mutation = useGenerateBillingProformaMutation(orderId, brandId);

  const isOutOfRange = dueDate < min || dueDate > max;
  const isSubmitDisabled = isOutOfRange || mutation.isPending;

  // Stable ref to onClose/triggerRef so the Esc effect can run once
  // (avoids stale-closure risk without a useCallback contract on the parent).
  const onCloseRef = useRef(onClose);
  const triggerRefRef = useRef(triggerRef);
  useEffect(() => {
    onCloseRef.current = onClose;
    triggerRefRef.current = triggerRef;
  }, [onClose, triggerRef]);

  const handleClose = () => {
    onCloseRef.current();
    // Return focus to trigger button
    setTimeout(() => {
      triggerRefRef.current?.current?.focus();
    }, 0);
  };

  // Auto-focus date input on open
  useEffect(() => {
    dateInputRef.current?.focus();
  }, []);

  // Esc closes modal (stops propagation so outer modal stays open).
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onCloseRef.current();
        setTimeout(() => {
          triggerRefRef.current?.current?.focus();
        }, 0);
      }
    };
    document.addEventListener("keydown", handleKeyDown, { capture: true });
    return () =>
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
  }, []);

  // Focus trap
  const handleTabTrap = (e: React.KeyboardEvent) => {
    if (e.key !== "Tab") return;
    const modal = modalRef.current;
    if (!modal) return;
    const focusable = modal.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const handleSubmit = async () => {
    if (isSubmitDisabled) return;
    try {
      await mutation.mutateAsync(dueDate);
      handleClose();
    } catch {
      // Error toast already shown by mutation hook; dialog stays open (AC-4)
    }
  };

  const describedBy = isOutOfRange
    ? "proforma-due-date-error proforma-due-date-caption"
    : "proforma-due-date-caption";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60]">
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={handleTabTrap}
        className="bg-white rounded-lg max-w-md w-full mx-4 shadow-xl"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
          <h2 id={titleId} className="text-base font-semibold text-neutral-900">
            Generuj proformę
          </h2>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Zamknij"
            className="text-neutral-400 hover:text-neutral-600"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          {/* L7: order context — the parent modal is hidden behind the overlay,
              so without this the admin cannot tell which order they are on. */}
          <p className="text-xs text-neutral-500" data-testid="proforma-order-context">
            Zamówienie {order.orderNumber}
            {order.tenantName ? ` — ${order.tenantName}` : ""}
          </p>
          <div>
            <label
              htmlFor="proforma-due-date"
              className="block text-sm font-medium text-neutral-700 mb-1"
            >
              Termin płatności
            </label>
            <input
              ref={dateInputRef}
              id="proforma-due-date"
              type="date"
              min={min}
              max={max}
              value={dueDate}
              disabled={mutation.isPending}
              onChange={(e) => setDueDate(e.target.value)}
              aria-invalid={isOutOfRange || undefined}
              aria-describedby={describedBy}
              className={`w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50 ${
                isOutOfRange
                  ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                  : "border-neutral-300"
              }`}
            />
            <p
              id="proforma-due-date-caption"
              className="text-xs text-neutral-400 mt-1"
            >
              Domyślnie: początek nowego okresu (koniec obecnej subskrypcji).
              Możesz ustawić wcześniejszą datę, np. koniec promocji.
            </p>
            {isOutOfRange && (
              <p
                id="proforma-due-date-error"
                role="alert"
                aria-live="polite"
                className="text-xs text-red-600 mt-1"
              >
                Termin musi mieścić się w zakresie {min} – {max}
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-neutral-200 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-sm border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50"
          >
            Anuluj
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            className="px-4 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
          >
            {mutation.isPending && (
              <div
                className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"
                aria-hidden="true"
              />
            )}
            {mutation.isPending ? "Generuję..." : "Generuj"}
          </button>
        </div>
      </div>
    </div>
  );
};
